@if(showPreview) {
    <mat-card class="export-preview-card">
        <mat-card-header>
            <mat-card-title>
                <div class="d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center gap-2">
                        <mat-icon>visibility</mat-icon>
                        <span>Export Preview</span>
                        @if(!exportOptions.flattenNestedHeaders && hasNestedColumns()) {
                            <span class="nested-indicator">
                                <i class="pi pi-sitemap"></i>
                                Multi-level Headers
                            </span>
                        }
                    </div>
                    <div class="preview-badge">
                        <small class="text-muted ps-2">First {{ maxRows }} rows</small>
                    </div>
                </div>
            </mat-card-title>
            <mat-card-subtitle>Preview based on: {{ getPreviewScope() }}</mat-card-subtitle>
        </mat-card-header>
    
        <mat-card-content>
            @if(previewLoading()) {
                <div class="text-center p-4">
                    <p-progressSpinner 
                        strokeWidth="3" 
                        [style]="{ width: '50px', height: '50px' }">
                    </p-progressSpinner>
                    <p>Generating preview...</p>
                </div>
            } @else if(previewError()) {
                <div class="text-center p-4">
                    <mat-icon class="text-danger" style="font-size: 48px; height: 48px; width: 48px;">
                        error_outline
                    </mat-icon>
                    <p class="mt-3 text-danger">{{ previewError() }}</p>
                    <p-button 
                        label="Retry" 
                        icon="pi pi-refresh" 
                        size="small"
                        [outlined]="true"
                        (onClick)="generatePreview()">
                    </p-button>
                </div>
            } @else if(previewData().length > 0) {
                <div class="preview-table-container">
                    @if(exportOptions.flattenNestedHeaders) {
                        <p-table [value]="previewData()" 
                                [scrollable]="true" 
                                scrollHeight="400px"
                                styleClass="p-datatable-sm p-datatable-gridlines"
                                [tableStyle]="{ 'min-width': '50rem' }">

                            <ng-template pTemplate="header">
                                <tr>
                                    @for(header of previewHeaders(); track header) {
                                        <th [pTooltip]="header" tooltipPosition="top">
                                            {{ header }}
                                        </th>
                                    }
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
                                <tr [class.aggregate-row]="isAggregateRow(row)">
                                    @for(header of previewHeaders(); track header) {
                                        <td [class.aggregate-cell]="isAggregateRow(row)">
                                            {{ row[header] }}
                                        </td>
                                    }
                                </tr>
                            </ng-template>
                        </p-table>
                    } @else {
                        <div class="nested-preview-table">
                            <table class="nested-table">
                                <thead>
                                <!-- Generate multiple header rows for nested structure -->
                                @for(headerRow of nestedHeaderRows(); track $index; let rowIdx = $index) {
                                    <tr class="header-row-level-{{ rowIdx }}">
                                    @for(cell of headerRow.cells; track cell.field || $index) {
                                        <th 
                                        [attr.colspan]="cell.colspan"
                                        [class.parent-header]="!cell.isLeaf"
                                        [class.leaf-header]="cell.isLeaf"
                                        [class.level-0]="cell.level === 0"
                                        [class.level-1]="cell.level === 1"
                                        [class.level-2]="cell.level === 2"
                                        [pTooltip]="cell.value"
                                        tooltipPosition="top">
                                        {{ cell.value }}
                                        </th>
                                    }
                                    </tr>
                                }
                                </thead>
                                <tbody>
                                @for(row of previewDataArray(); track $index; let rowIdx = $index) {
                                    <tr [class.aggregate-row]="isAggregateRowByIndex(rowIdx)">
                                    @for(value of row; track $index) {
                                        <td [class.aggregate-cell]="isAggregateRowByIndex(rowIdx)">
                                        {{ value }}
                                        </td>
                                    }
                                    </tr>
                                }
                                </tbody>
                            </table>
                        </div>
                    }
                    
                </div>
                    
                <!-- Preview Info Footer -->
                <div class="preview-info mt-3 p-2 border-top">
                    <div class="d-flex justify-content-between align-items-center">
                        <div class="d-flex gap-3">
                            <small class="text-muted">
                                <i class="pi pi-list"></i>
                                Showing {{ getDataRowCount() }} data row{{ getDataRowCount() !== 1 ? 's' : '' }}
                            </small>
                            @if(hasAggregateRows()) {
                                <small class="text-primary">
                                    <i class="pi pi-calculator"></i>
                                    {{ getAggregateRowCount() }} aggregate row{{ getAggregateRowCount() !== 1 ? 's' : '' }}
                                </small>
                            }
                            @if(!exportOptions.flattenNestedHeaders && hasNestedColumns()) {
                                <small class="text-info">
                                <i class="pi pi-sitemap"></i>
                                {{ getHeaderRowCount() }} header row{{ getHeaderRowCount() !== 1 ? 's' : '' }}
                                </small>
                            }
                        </div>
                        <small class="text-muted">
                            Total export will contain {{ totalRows }} row{{ totalRows !== 1 ? 's' : '' }}
                        </small>
                    </div>
                </div>
                    
                <!-- Additional Export Info -->
                @if(exportOptions.includeAggregates) {
                    <div class="export-info mt-2 p-2 bg-blue-50 border-round">
                        <div class="d-flex align-items-start gap-2">
                            <i class="pi pi-info-circle text-blue-600 mt-1"></i>
                            <div>
                                <strong class="text-blue-600">Aggregates Included</strong>
                                <p class="text-sm text-muted mb-0">
                                    Summary rows will be placed at the <strong>{{ exportOptions.aggregatePosition }}</strong> of your export.
                                    @if(exportOptions.aggregateSeparatorRow) {
                                        A separator row will be added for clarity.
                                    }
                                </p>
                            </div>
                        </div>
                    </div>
                }
            } 
            
            @if(!exportOptions.flattenNestedHeaders && hasNestedColumns()) {
              <div class="export-info mt-2 p-2 bg-purple-50 border-round">
                <div class="d-flex align-items-start gap-2">
                  <i class="pi pi-sitemap text-purple-600 mt-1"></i>
                  <div>
                    <strong class="text-purple-600">Nested Column Structure</strong>
                    <p class="text-sm text-muted mb-0">
                      Your CSV will include {{ getHeaderRowCount() }} header rows to represent the column hierarchy.
                      Parent columns will span across their child columns for clear organization.
                    </p>
                  </div>
                </div>
              </div>
            } @else {
                <div class="text-center p-4">
                    <mat-icon class="text-muted" style="font-size: 48px; height: 48px; width: 48px;">
                        preview
                    </mat-icon>
                    <p class="mt-3 text-muted">No preview data available</p>
                    <p class="text-sm text-muted">Select export options and data to generate preview</p>
                </div>
            }
        </mat-card-content>

        @if(!previewLoading() && previewData().length > 0) {
            <mat-card-actions class="d-flex justify-content-end gap-2 p-2">
                <p-button 
                    label="Refresh Preview" 
                    icon="pi pi-refresh" 
                    size="small"
                    [outlined]="true"
                    (onClick)="generatePreview()">
                </p-button>
                <p-button 
                    label="Copy Preview" 
                    icon="pi pi-copy" 
                    size="small"
                    [outlined]="true"
                    (onClick)="copyPreviewToClipboard()"
                    pTooltip="Copy preview data to clipboard">
                </p-button>
            </mat-card-actions>
        }
    </mat-card>
}